import { Address } from "@/Common/Api/Contacts/Dto/Address";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { AddressControlDTO } from "@/Common/Components/Contacts/AddressControl";
import { ExpressEditMode } from "@/Common/Models/Patient/PatientEnums";
import { IPatientContactsModel } from "@/Common/Models/Patient/UI/Profile/IPatientContactsModel";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { AddressesEdit } from "./ContactComponents/AddressesEdit";
import { EmailsEdit } from "./ContactComponents/EmailsEdit";
import { PhonesEdit } from "./ContactComponents/PhonesEdit";

interface IPatientProfileContactsProps {
    model: IPatientContactsModel;
    autofocusOperation?: ExpressEditMode;
    autofocusId?: string;
}

class ContactsEdit extends BaseReactComponent<IPatientProfileContactsProps, null> {
    public render(): JSX.Element {
        const model = this.props.model;
        const addresses = model.get("Addresses").toArray();
        const phones = model.get("PhoneNumbers").toArray();

        const doNotEmail = model.get("DoNotEmail");
        const emails = model.get("Emails").toArray();
        const showWarningMessage = emails?.some(x => x.Address.trim().length);

        return (
            <div className="patient-prof scrollable" data-cy="patientProfile-contacts-edit">
                <div className="block separated">
                    <div className="flex-container heading">
                        <h3 className="thin-font">{cacheStore.GetString("PatientLabel")}</h3>
                    </div>
                    <div className="patient-profile fxd-wdth-container edit margin-lg-bottom">
                        <div className="tb">
                            <div className="tbc left">
                                <AddressesEdit
                                    addresses={addresses}
                                    onAdd={() => sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_AddAddress)}
                                    onRemove={address => sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_RemoveAddress, { value: address.ContactPoint_id })}
                                    onNotifyParentCallback={(address, dto) => this.onNotifyParentAddressCallback(address, dto)}
                                    onContactTypeChange={(value, index) => sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_AddressTypeSelect, { value: value, item: addresses[index] })}
                                    onPreferenceChange={(isChecked, index) => sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_AddressIsPreferredCheck, { value: isChecked, item: addresses[index] })}
                                />
                            </div>

                            <div className="tbc right">
                                <PhonesEdit
                                    phones={phones}
                                    onAdd={() => sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_AddPhone)}
                                    onRemove={phone => sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_RemovePhone, { value: phone.ContactPoint_id })}
                                    onNumberChange={(value, index) => sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_PhoneNumberChange, { value: value, item: phones[index] })}
                                    onTypeSelect={(value, index) => sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_PhoneTypeSelect, { value: value, item: phones[index] })}
                                    onCountrySelect={(value, index) => sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_PhoneCountrySelect, { value: value, item: phones[index] })}
                                    onExtChange={(value, index) => sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_PhoneExtChange, { value: value, item: phones[index] })}
                                    onNoteChange={(value, index) => sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_PhoneNotesChange, { value: value, item: phones[index] })}
                                    onDefaultNotificationChange={(isChecked, index) => sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_PhoneDefaultNotificationChange, { value: isChecked, item: phones[index] })}
                                    onIsPreferredChange={(isChecked, index) => sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_PhoneIsPreferredCheck, { value: isChecked, item: phones[index] })}
                                />
                                <EmailsEdit
                                    emails={emails}
                                    doNotEmail={doNotEmail}
                                    showWarningMessage={showWarningMessage}
                                    onDoNotEmailChange={isChecked => sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_DoNotEmailChange, { checked: isChecked })}
                                    onAdd={() => sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_AddEmail)}
                                    onRemove={email => sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_RemoveEmail, { value: email.ContactPoint_id })}
                                    onContactTypeChange={(value, index) => sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_EmailTypeSelect, { value: value, item: emails[index] })}
                                    onOkToContactChange={(isChecked, index) => sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_EmailCanContactChange, { item: emails[index] })}
                                    onDefaultNotificationChange={(isChecked, index) => sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_EmailDefaultNotificationChange, { checked: isChecked, item: emails[index] })}
                                    onEmailChange={(value, index) => sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_EmailAddressChange, { value: value, item: emails[index] })}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    public componentDidMount(): void {
        $(window).trigger("resize");
    }

    private onNotifyParentAddressCallback(address: Address, dto: AddressControlDTO): void {
        if (dto.civicAddressEdit !== address.StreetAddress) {
            sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_AddressChange, {
                item: address,
                value: dto.civicAddressEdit,
                id: `ContactEdit_Address_${address.ContactPoint_id}_Street`
            }, 0);
        }
        if (dto.cityEdit !== address.City) {
            sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_AddressCityChange, {
                item: address,
                value: dto.cityEdit,
                id: `ContactEdit_Address_${address.ContactPoint_id}_City`
            }, 0);
        }
        if (dto.postalCodeEdit !== address.ZipCode) {
            sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_AddressZipCodeChange, {
                item: address,
                value: dto.postalCodeEdit,
                id: `ContactEdit_Address_${address.ContactPoint_id}_PostalCode`
            }, 0);
        }
        if (dto.selectedCountryLookupId !== address.CountryLookupId) {
            sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_AddressCountrySelect, {
                item: address,
                value: dto.selectedCountryLookupId,
                id: `ContactEdit_Address_${address.ContactPoint_id}_Country`
            }, 0);
        }
        if (dto.selectedRegionLookupId !== address.CountryLookupId) {
            sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_AddressCountryRegionSelect, {
                item: address,
                value: dto.selectedRegionLookupId,
                id: `ContactEdit_Address_${address.ContactPoint_id}_Region`
            }, 0);
        }
    }
}

export { ContactsEdit, IPatientProfileContactsProps };
